import type { CodeSessionPullRequest } from '@/lib/code-sessions/pull-request';
import type { QueryClient } from '@tanstack/react-query';

import { codeSessionWorkspaceKeys } from './workspace-keys';

import { codeSessionPullRequestState } from '@/lib/code-sessions/pull-request-state';

type PullRequestIdentity = { owner: string; repo: string; number: string };
type PullRequestUpdate = {
  state?: 'open' | 'closed';
  draft?: boolean;
  merged?: boolean;
};

const isSessionPullRequestQuery = ({ queryKey }: { queryKey: readonly unknown[] }) =>
  queryKey.length === 3 && queryKey[2] === 'pull-request';

/**
 * Keep session icons in step with PR actions without waiting for their GitHub lookup to poll.
 * The returned rollback restores an icon only while it still holds this patch's write; a lookup
 * result or another action's patch that landed since owns it, even when it agrees with the patch.
 */
export async function patchCodeSessionPullRequests(
  client: QueryClient,
  identity: PullRequestIdentity,
  update: PullRequestUpdate,
): Promise<() => void> {
  const url =
    `https://github.com/${identity.owner}/${identity.repo}/pull/${identity.number}`.toLowerCase();
  const matches = (pr: CodeSessionPullRequest | null | undefined): pr is CodeSessionPullRequest =>
    pr?.url.toLowerCase() === url;
  const entries = client
    .getQueriesData<CodeSessionPullRequest | null>({
      queryKey: codeSessionWorkspaceKeys.root,
      predicate: isSessionPullRequestQuery,
    })
    .filter(([, pr]) => matches(pr));

  // An older lookup must not put the pre-action state back after this write.
  await Promise.all(entries.map(([queryKey]) => client.cancelQueries({ queryKey, exact: true })));

  const snapshots = entries.flatMap(([queryKey]) => {
    const previous = client.getQueryData<CodeSessionPullRequest | null>(queryKey);
    if (!matches(previous)) {
      return [];
    }
    const state = codeSessionPullRequestState({
      state: update.state ?? previous.state,
      draft: update.draft ?? previous.state === 'draft',
      merged: update.merged ?? previous.state === 'merged',
    });
    const optimistic: CodeSessionPullRequest = {
      ...previous,
      state,
      ...(state === 'closed' || state === 'merged'
        ? { hasConflicts: false, reviewDecision: null }
        : {}),
    };
    client.setQueryData(queryKey, optimistic);
    return [{ queryKey, previous, revision: client.getQueryState(queryKey)?.dataUpdateCount }];
  });

  return () => {
    for (const { queryKey, previous, revision } of snapshots) {
      if (client.getQueryState(queryKey)?.dataUpdateCount === revision) {
        client.setQueryData(queryKey, previous);
      }
    }
  };
}

/**
 * A lookup still loading its first result has no PR to match, so a patch can neither update nor
 * cancel it, and its response may predate the action. Once the action is confirmed, drop that
 * response and read the lookup again, without waiting for the new read.
 */
export async function refetchLoadingCodeSessionPullRequests(client: QueryClient): Promise<void> {
  const loading = client
    .getQueriesData<CodeSessionPullRequest | null>({
      queryKey: codeSessionWorkspaceKeys.root,
      predicate: isSessionPullRequestQuery,
    })
    .filter(
      ([queryKey, pr]) =>
        pr === undefined && client.getQueryState(queryKey)?.fetchStatus === 'fetching',
    );
  await Promise.all(loading.map(([queryKey]) => client.cancelQueries({ queryKey, exact: true })));
  for (const [queryKey] of loading) {
    void client.invalidateQueries({ queryKey, exact: true });
  }
}
